import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Wrench, CheckCircle2, Layers, ExternalLink } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_LABEL } from "@/lib/constants";
import { fmtDate, fmtDateTime, money, deviceLabel } from "@/lib/auditDates";
import { rowHref } from "@/components/orders/OrderRowCard";

const FINALIZED = ["REPAIRED", "READY", "DELIVERED"];
const CLOSED_OTHER = ["NOT_REPAIRABLE", "CLIENT_REJECTED"];

function statusCounts(devs) {
  const c = { ready: 0, repairing: 0, delivered: 0, repaired: 0, closed: 0 };
  devs.forEach((d) => {
    if (d.status === "READY") c.ready++;
    else if (d.status === "DELIVERED") c.delivered++;
    else if (d.status === "REPAIRED") c.repaired++;
    else if (CLOSED_OTHER.includes(d.status)) c.closed++;
    else c.repairing++;
  });
  return c;
}

const devicePrice = (r) => (r.price != null ? money(r.price) : "Sin precio");
const priceNote = (r) => ({
  final: "precio final",
  approved: "presupuesto aprobado",
}[r.price_kind] || (r.budget_total > 0 ? "presupuesto sin aprobar" : "sin presupuesto"));

const DeviceAccordion = ({ r }) => {
  const [open, setOpen] = useState(false);
  const tid = `group-device-${r.order_number}-${r.device_index}`;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={tid}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left min-h-[48px]" data-testid={`${tid}-toggle`}>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="text-[11px] font-bold text-indigo-700 whitespace-nowrap">Equipo {r.device_index} de {r.device_count}</span>
            <span className="text-sm text-slate-700 truncate">{deviceLabel(r) || r.category_name}</span>
          </span>
          <span className="mt-0.5 block"><StatusBadge status={r.status} size="sm" /></span>
        </span>
        <span className="font-mono text-sm font-semibold text-slate-900 whitespace-nowrap shrink-0" data-testid={`${tid}-price`}>{devicePrice(r)}</span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1 text-xs text-slate-600 border-t border-slate-100" data-testid={`${tid}-detail`}>
          <div><span className="text-slate-400">Servicio:</span> {r.services || "Sin servicio cargado"}</div>
          <div><span className="text-slate-400">Precio del equipo:</span> {devicePrice(r)} ({priceNote(r)})</div>
          <div className="flex items-center gap-1"><Wrench size={12} /> {r.technician_name || "Sin asignar"}</div>
          {r.finalized_at && <div className="flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 size={12} /> Finalizada: {fmtDateTime(r.finalized_at)}</div>}
          {r.pickup_at && <div>Retirado: {fmtDateTime(r.pickup_at)}</div>}
          <div className="text-slate-400 truncate">Falla: {r.problem_reported || "—"}</div>
          <Link to={rowHref(r)} className="inline-flex items-center gap-1 mt-1 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold" data-testid={`${tid}-open`}>
            <ExternalLink size={12} /> Abrir expediente del equipo
          </Link>
        </div>
      )}
    </div>
  );
};

export const OrderGroupCard = ({ rows }) => {
  const r0 = rows[0];
  const devs = r0.order_devices || [];
  const c = statusCounts(devs);
  const done = devs.filter((d) => FINALIZED.includes(d.status)).length;
  const matched = new Set(rows.map((r) => r.device_id));
  const others = devs.filter((d) => !matched.has(d.device_id));
  const techs = [...new Set(rows.map((r) => r.technician_name).filter(Boolean))];
  const paid = r0.order_paid_amount;
  const chips = [
    [c.ready, "listos para retirar", "bg-emerald-50 text-emerald-800 border-emerald-200"],
    [c.repaired, "reparados", "bg-teal-50 text-teal-800 border-teal-200"],
    [c.repairing, "en proceso", "bg-amber-50 text-amber-800 border-amber-200"],
    [c.delivered, "entregados", "bg-slate-100 text-slate-700 border-slate-200"],
    [c.closed, "no reparados/rechazados", "bg-rose-50 text-rose-700 border-rose-200"],
  ].filter(([n]) => n > 0);
  return (
    <div className="card-base p-4 flex flex-col gap-3 border-l-4 !border-l-indigo-500" data-testid={`order-group-${r0.order_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/orders/${r0.order_id}`} className="font-mono text-xs text-blue-600 tracking-wider hover:underline">{r0.order_number}</Link>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5" data-testid={`order-group-count-${r0.order_number}`}>
              <Layers size={11} /> {r0.device_count} equipos
            </span>
          </div>
          <div className="font-heading font-bold text-lg truncate">{r0.customer_name || "Cliente"}</div>
          <div className="text-xs text-slate-500 truncate">{r0.branch_name || "—"} · Rec. {fmtDate(r0.received_at)}{techs.length > 0 && ` · ${techs.join(", ")}`}</div>
        </div>
        <div className="text-right shrink-0" data-testid={`order-group-total-${r0.order_number}`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Total de la orden</div>
          <div className="font-heading font-extrabold text-xl text-slate-900">{money(r0.order_total)}</div>
          <div className="text-[10px] text-slate-500" data-testid={`order-group-paid-${r0.order_number}`}>
            {paid != null ? <>Cobro registrado {money(paid)} · Saldo {money(Math.max(r0.order_total - paid, 0))}</> : "Sin cobros registrados"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`order-group-status-${r0.order_number}`}>
        <span className="text-xs font-semibold text-slate-700 mr-1">{done} de {devs.length} finalizados</span>
        {chips.map(([n, label, cls]) => (
          <span key={label} className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${cls}`}>{n} {label}</span>
        ))}
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => <DeviceAccordion key={r.row_id} r={r} />)}
      </div>
      {others.length > 0 && (
        <div className="text-[11px] text-slate-500" data-testid={`order-group-unmatched-${r0.order_number}`}>
          Coinciden con el filtro: {rows.map((r) => `Equipo ${r.device_index}`).join(", ")}. No coinciden: {others.map((d) => `Equipo ${d.device_index} (${STATUS_LABEL[d.status] || d.status})`).join(", ")}.
        </div>
      )}
    </div>
  );
};

// Agrupa filas por orden conservando el orden de la primera aparición.
export function groupRowsByOrder(rows) {
  const map = new Map();
  rows.forEach((r) => {
    if (!map.has(r.order_id)) map.set(r.order_id, []);
    map.get(r.order_id).push(r);
  });
  return [...map.values()].map((g) => g.sort((a, b) => a.device_index - b.device_index));
}
